import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, LogIn, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error("Bitte E-Mail und Passwort eingeben");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const { token, user } = response.data;
      
      // Save to localStorage
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      
      toast.success(`Willkommen, ${user.name}!`);
      onLogin(user, token);
      navigate("/");
    } catch (error) {
      console.error("Login error:", error);
      if (error.response?.status === 401) {
        toast.error("Ungültige Anmeldedaten");
      } else {
        toast.error("Anmeldung fehlgeschlagen");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
      {/* Warning Tape */}
      <div className="warning-tape fixed top-0 left-0 right-0" />
      
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-yellow-500 flex items-center justify-center rounded-sm mx-auto mb-4">
            <AlertTriangle className="w-10 h-10 text-black" strokeWidth={2.5} />
          </div>
          <h1 className="text-3xl font-black tracking-tighter text-zinc-50" style={{ fontFamily: 'Chivo, sans-serif' }}>
            UNFALLMELDE TOOL
          </h1>
          <p className="text-sm text-zinc-500 uppercase tracking-widest mt-1">Arbeitsschutz</p>
        </div>

        {/* Login Form */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-8">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-50 mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Anmeldung
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                E-Mail
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="ihre@email.de"
                className="bg-zinc-950 border-zinc-800 text-zinc-50 placeholder:text-zinc-600 focus:border-yellow-500 focus:ring-yellow-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                Passwort
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="login-password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="••••••••"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 placeholder:text-zinc-600 focus:border-yellow-500 focus:ring-yellow-500 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full bg-yellow-500 text-black hover:bg-yellow-400 font-semibold h-11"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Anmelden...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Anmelden
                </>
              )}
            </Button>
            
            <div className="text-center">
              <Link 
                to="/passwort-vergessen" 
                data-testid="forgot-password-link"
                className="text-sm text-zinc-500 hover:text-yellow-500 transition-colors"
              >
                Passwort vergessen?
              </Link>
            </div>
          </form>
        </div>

        <p className="text-center text-zinc-600 text-sm mt-6">
          Zugang nur für autorisierte Benutzer
        </p>

        <div className="flex justify-center gap-4 mt-4">
          <Link 
            to="/impressum" 
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            Impressum
          </Link>
          <span className="text-zinc-700">|</span>
          <Link 
            to="/datenschutz" 
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            Datenschutz
          </Link>
        </div>
      </div>
    </div>
  );
}
